import { Divider, Typography } from '@mui/material';
import {
  isHeading,
  isLink,
  isParagraph,
  isThematicBreak,
} from 'datocms-structured-text-utils';
import { Fragment } from 'react';
import { renderMarkRule, renderNodeRule, StructuredText } from 'react-datocms';

import { isExternalLink } from 'utils/routes.utils';
import {
  extractAnchorFromHeading,
  isNodeEmpty,
} from 'utils/structured-text.utils';

import Link from 'components/@common/Link';

import { Props } from './types';

const CUSTOM_NODE_RULES = [
  renderNodeRule(isHeading, ({ node, children, key }) => {
    const level = (node.level < 4 ? node.level + 2 : 6) as 3 | 4 | 5 | 6;
    const anchor = extractAnchorFromHeading(node);

    return (
      <Typography
        id={anchor}
        variant={`h${level}`}
        component={`h${node.level}`}
        key={key}
      >
        {children}
      </Typography>
    );
  }),

  renderNodeRule(isParagraph, ({ children, ancestors, key, node }) => {
    // Block quotes are wrapped in p tags, unwrap them
    if (ancestors[0].type === 'blockquote')
      return <Fragment key={key}>{children}</Fragment>;

    if (isNodeEmpty(node)) return <br key={key} />;

    return (
      <Typography variant="body2" key={key}>
        {children}
      </Typography>
    );
  }),
  renderNodeRule(isThematicBreak, ({ key }) => (
    <Divider key={key} sx={{ borderColor: 'grey.200', my: 6 }} />
  )),
  renderNodeRule(isLink, ({ node, children, key }) => {
    const rest =
      node.meta?.reduce<Record<string, string>>((memo, meta) => {
        memo[meta.id] = meta.value;
        return memo;
      }, {}) ?? {};

    return (
      <Link
        rel={rest['rel'] ?? 'noopener noreferrer'}
        href={node.url}
        target={isExternalLink(node.url) ? '_blank' : '_self'}
        variant="body1"
        underline="always"
        key={key}
        color="inherit"
        letterSpacing={0}
        textTransform="none"
        {...rest}
      >
        {children}
      </Link>
    );
  }),
];

const CUSTOM_MARK_RULES = [
  renderMarkRule('strong', ({ children, key }) => (
    <strong key={key}>{children}</strong>
  )),
  renderMarkRule('italic', ({ children, key }) => <i key={key}>{children}</i>),
];

const StructuredContent = ({ content, customNodeRules = [] }: Props) => {
  const NODE_RULES = [...new Set([...customNodeRules, ...CUSTOM_NODE_RULES])];

  return (
    <StructuredText
      data={content}
      customNodeRules={NODE_RULES}
      customMarkRules={CUSTOM_MARK_RULES}
    />
  );
};

export default StructuredContent;
